import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from "lucide-react";
import { track } from "../../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_SRC = "/assets/pgps-logo.jpg";

const TikTokIcon = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.321 5.562a5.124 5.124 0 0 1-3.414-1.267 5.124 5.124 0 0 1-1.537-2.972h-3.131v12.388a3.087 3.087 0 1 1-2.17-2.948V7.57a6.265 6.265 0 1 0 5.436 6.196V8.48a8.265 8.265 0 0 0 4.816 1.541V6.89a5.11 5.11 0 0 1 0-1.328z" />
  </svg>
);

const SOCIAL_ICONS = [
  { key: "facebook", label: "Facebook", Icon: Facebook, testid: "social-facebook" },
  { key: "instagram", label: "Instagram", Icon: Instagram, testid: "social-instagram" },
  { key: "tiktok", label: "TikTok", Icon: TikTokIcon, testid: "social-tiktok" },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, testid: "social-linkedin" },
];

const FEDERAL_LINKS = [
  { to: "/federal-dla", label: "Federal & DLA Supply Solutions" },
  { to: "/prime-contractors", label: "Prime Contractor Support" },
  { to: "/capability-statement", label: "Capability Statement" },
  { to: "/supplier-verification", label: "Supplier Verification" },
  { to: "/federal-compliance", label: "Federal Compliance" },
  { to: "/request-a-quote?type=federal", label: "Submit an RFQ" },
];

const COMPANY_LINKS = [
  { to: "/procurement", label: "Procurement & Sourcing" },
  { to: "/industrial-mro", label: "Industrial & MRO" },
  { to: "/government", label: "Government Contracting & Capabilities" },
  { to: "/capability-statement", label: "Capability Statement" },
  { to: "/logistics", label: "Logistics" },
  { to: "/procurement-as-a-service", label: "Procurement-as-a-Service" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
  { to: "/guides", label: "Buyer Guides" },
  { to: "/contact", label: "Contact" },
  { to: "/portal", label: "Client Portal" },
  { to: "/supplier", label: "Supplier Portal" },
  { to: "/request-a-quote", label: "Submit an RFQ" },
];

export function SiteFooter() {
  const [social, setSocial] = useState({});
  useEffect(() => {
    axios.get(`${API}/settings/public`).then(({ data }) => setSocial(data?.social_links || {})).catch(() => {});
  }, []);
  const activeSocials = SOCIAL_ICONS.filter((s) => (social[s.key] || "").trim().startsWith("http"));
  return (
    <footer data-testid="site-footer" className="dark-section pt-16 pb-8 mt-0" itemScope itemType="https://schema.org/Organization">
      <div className="pg-container">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="bg-white rounded-xl p-3 w-fit overflow-hidden">
              <div className="h-20 w-[120px] overflow-hidden flex items-center justify-center">
                <img src={LOGO_SRC} alt="Patrick Global" className="h-[150%] w-auto max-w-none object-contain" />
              </div>
            </div>
            <p className="mt-5 text-white/75 text-[15px] leading-relaxed max-w-md">
              <strong className="text-white">Patrick Global</strong> helps businesses, government organizations,
              contractors, and institutions source products, equipment, and supplies from qualified suppliers —
              simplifying procurement, controlling costs, and coordinating delivery.
            </p>
            <p className="mt-3 text-white/60 text-[13px] leading-relaxed">
              Industrial Procurement • Government Solutions • Strategic Sourcing • Logistics
            </p>
            {activeSocials.length > 0 && (
            <div className="mt-6 flex items-center gap-3" data-testid="social-links">
              {activeSocials.map(({ key, label, Icon, testid }) => (
                <a key={label} href={social[key]} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={testid}
                  className="w-10 h-10 rounded-full border border-white/20 bg-white/5 text-white/85 hover:text-white hover:bg-[var(--pg-accent)] hover:border-[var(--pg-accent)] flex items-center justify-center transition-colors">
                  <Icon size={18} />
                </a>
              ))}
            </div>
            )}
          </div>
          <div className="md:col-span-3" data-testid="footer-federal-section">
            <h4 className="text-white font-[Poppins] font-bold text-[15px] tracking-wider uppercase">Federal Supply Capabilities</h4>
            <ul className="mt-4 space-y-2 text-[14.5px]">
              {FEDERAL_LINKS.map((l) => (
                <li key={l.to}><Link to={l.to} data-testid={`footer-federal-${l.to.split("?")[0].slice(1)}`} className="text-white/75 hover:text-[var(--pg-accent)] transition-colors">{l.label}</Link></li>
              ))}
            </ul>
            <dl className="mt-5 text-[13px] text-white/75 space-y-1" data-testid="footer-identifiers">
              <div className="flex gap-2"><dt className="font-[Poppins] font-semibold text-white">UEI:</dt><dd>MK66QMXSC4A4</dd></div>
              <div className="flex gap-2"><dt className="font-[Poppins] font-semibold text-white">CAGE:</dt><dd>12UK2</dd></div>
            </dl>
          </div>
          <div className="md:col-span-2">
            <h4 className="text-white font-[Poppins] font-bold text-[15px] tracking-wider uppercase">Company</h4>
            <ul className="mt-4 space-y-2 text-[14.5px]">
              {COMPANY_LINKS.map((l) => (
                <li key={l.to}><Link to={l.to} className="text-white/75 hover:text-[var(--pg-accent)] transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-white font-[Poppins] font-bold text-[15px] tracking-wider uppercase">Contact</h4>
            <ul className="mt-4 space-y-3 text-[14.5px] text-white/75">
              <li className="flex gap-3"><MapPin size={16} className="mt-1 text-[var(--pg-orange)] flex-shrink-0" /> 8700 Commerce Park Drive, Suite 238, Houston, Texas 77036</li>
              <li className="flex gap-3"><Phone size={16} className="text-[var(--pg-orange)]" /> <a href="tel:+12819019914" data-testid="footer-phone-link" onClick={() => track("phone_click", { source: "footer" })} className="hover:text-white">281-901-9914</a></li>
              <li className="flex gap-3"><Mail size={16} className="text-[var(--pg-orange)]" /> <a href="mailto:info@pgpsusa.com" data-testid="footer-email-link" onClick={() => track("email_click", { source: "footer" })} className="hover:text-white">info@pgpsusa.com</a></li>
            </ul>
            <Link to="/request-a-quote" data-testid="footer-request-quote-link" className="btn-primary mt-6 inline-flex !py-3 !px-5 text-[13.5px]">
              Submit an RFQ
            </Link>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
          <p className="text-white/55 text-[13px]">© {new Date().getFullYear()} Patrick Global Procurement Services. All rights reserved.</p>
          <p className="text-white/55 text-[13px]">We Source. We Procure. We Deliver. · Houston, TX</p>
        </div>
      </div>
    </footer>
  );
}
